import {
  appConfig,
  AppManager,
  CacheManager,
  logger,
  PopupManager,
  AnalyticsManager,
  ViewHistoryManager,
} from "@common";
import $ from "jquery";
import * as PANOLENS from "panolens";
import * as THREE from "three";
import InfoCaptionSpot from "./InfoCaptionSpot";
import InfoLinkSpot from "./InfoLinkSpot";
import InfoMenuDivider from "./InfoMenuDivider";
import InfoMenuLinkSpot from "./InfoMenuLinkSpot";
import InfoMenuSpot from "./InfoMenuSpot";
import InfoProductViewSpot from "./InfoProductViewSpot";
import InfoVideoLabelSpot from "./InfoVideoLabelSpot";


export default class PanoramaController {
  constructor(viewer, config, asWelcomeScreen = false) {
    this.viewer = viewer;
    this.config = config;
    this.asWelcomeScreen = asWelcomeScreen;

    this.panorama = new PANOLENS.ImagePanorama(this.config.image);
    this.panorama.userData.controller = this;

    // configure events
    this.panorama.addEventListener('hover', this.onHover.bind(this));

    this.panorama.addEventListener('progress', this.onProgress.bind(this));
    // this.panorama.addEventListener('load', this.onLoad.bind(this));
    this.panorama.addEventListener('enter', this.onEnter.bind(this));
    this.panorama.addEventListener('enter-fade-start', this.onEnterFadeStart.bind(this));
    this.panorama.addEventListener('enter-fade-complete', this.onEnterFadeComplete.bind(this));
    this.panorama.addEventListener('leave', this.onLeave.bind(this));
  }


  load() {
    const deferred = new $.Deferred();
    const onLoad = (event) => {
      logger.info(`${this.panoramaId} | onLoad`, event);
      this.panorama.removeEventListener('load', onLoad);
      deferred.resolve();
    };
    this.panorama.addEventListener('load', onLoad);
    this.panorama.load();
    return deferred.promise();
  }


  showAsWelcomeScreen() {
    this.viewer.add(this.panorama);
    const initPos = new THREE.Vector3().fromArray(this.config.initialLookAt);
    this.viewer.tweenControlCenter(initPos, 0);
    this.setupFov();
  }


  startAsWelcomeScreen() {
    this.asWelcomeScreen = false;
    this.panorama.onEnter();
  }


  start() {
    // 初期視点位置
    const initPos = new THREE.Vector3().fromArray(this.config.initialLookAt);
    this.viewer.tweenControlCenter(initPos, 0);
    if (!appConfig.debug.measureMode) {
      this.setupMenu();
      this.setupCaptions();
      this.setupProductView();
      this.setupVideo();
      this.setupLinking();
    }
    this.setupFov();
    if (appConfig.debug.visiblePanoramaId) AppManager.showInTextWidget(this.panoramaId + ' ' + this.linkLabel);
    AppManager.setCurrentFloor(this.config.floor);
    AppManager.showEraButton(this.config.eraButton != undefined && this.config.eraButton);
    AppManager.showHomeButton(this.config.homeButton != undefined && this.config.homeButton);
    AppManager.showCommentaryButton(this.config.commentary != undefined && this.config.commentary);
    AppManager.showTaishiButton(this.config.taishiButton != undefined && this.config.taishiButton);
    if (appConfig.autoRotateEnabled) {
      setTimeout(() => {
        this.viewer.enableAutoRate();
      }, 3000);
    }
    AnalyticsManager.sendPanorama(this.panoramaId, this.config.linkLabel);
    ViewHistoryManager.savePanorama(this.panoramaId);
  }


  onProgress(event) {
    const p = Math.floor(event.progress.loaded / event.progress.total * 100);
    logger.info(`${this.panoramaId} | onProgress`, `${p}%`, event);
    // AppManager.showInTextWidget(`${p}%`);
    AppManager.setLoading(true);
  }


  onLoad(event) {
    logger.info(`${this.panoramaId} | onLoad`, event);
  }


  onEnter(event) {
    logger.info(`${this.panoramaId} | onEnter`, event);
  }


  onEnterFadeStart(event) {
    logger.info(`${this.panoramaId} | onEnterFadeStart`, event);   
    AppManager.setLoading(false);
    CacheManager.check(this.panoramaId, 'onEnter');    
    if (!this.asWelcomeScreen) {
      this.start();
    }
  }


  onEnterFadeComplete(event) {
    logger.info(`${this.panoramaId} | onEnterFadeComplete`, event);
    if (this.config.minimap != undefined && this.config.minimap) {
      const initAngle = THREE.Math.radToDeg(this.viewer.camera.rotation.y);
      AppManager.showMinimap(true, initAngle);
    }
  }


  handleIntro() {
    if (this.config.intro && this.config.intro.content && !appConfig.debug.suppressIntro) {
      const contentId = this.config.intro.content;
      logger.info(`has viewed ${contentId} : ${AppManager.hasViewedIntro(this.panoramaId)}`);
      if (!AppManager.hasViewedIntro(contentId)) {
        if (this.hasProductView) {
          // DIGISKIN TESTERとMAKE ME UPはイントロ動画終了後第一階層開く
          PopupManager.openVideo(this.config.intro,
            () => {
              const conf = this.config.productView[0];
              PopupManager.openIframe(conf.action);
            },
            () => {
              AnalyticsManager.sendVideo(this.config.intro.content, 'playedVideoEndByIntro');
            });
        } else {
          PopupManager.openVideo(this.config.intro, null,
            () => {
              AnalyticsManager.sendVideo(this.config.intro.content, 'playedVideoEndByIntro');
            });
        }
        AnalyticsManager.sendVideo(this.config.intro.content, 'playByIntro');
        //AppManager.markViewedIntro(contentId);
      }
    }
  }


  onLeave(event) {
    logger.info(`${this.panoramaId} | onLeave`, event);
    this.viewer.remove(this.panorama);
    logger.debug("## current panorama in scene", this.viewer.getScene().children, this.viewer.getScene().children.map(p => p.src));
    AppManager.showMinimap(false);
  }


  hideObject() {
    logger.info(`${this.panoramaId} | hideObject`);
    this.hideMenu();
    this.hideCaptions();
    this.hideProductView();
    this.hideVideo();
    this.hideLinks();
    this.panorama.toggleInfospotVisibility(false);
  }


  setupMenu() {
    if (!this.hasMenu) return;
    if (this.menuSpotHolder === undefined) {
      this.menuSpotHolder = [];
      this.config.menu.forEach(menuConf => {
        if (menuConf.divider) {
          this.menuSpotHolder.push(new InfoMenuDivider(this.panorama, menuConf));
        } else if (menuConf.action.type === "link") {
          this.menuSpotHolder.push(new InfoMenuLinkSpot(this.panorama, menuConf));
        } else {
          this.menuSpotHolder.push(new InfoMenuSpot(this.panorama, menuConf));
        }
      });
    } else {
      this.menuSpotHolder.forEach(menuSpot => {
        menuSpot.show();
      });
    }
  }


  hideMenu() {
    if (!this.hasMenu) return;
    if (this.menuSpotHolder === undefined) return;
    this.menuSpotHolder.forEach(menuSpot => {
      menuSpot.hide();
    });
  }


  setupCaptions() {
    if (!this.hasCaptions) return;
    if (this.captionSpotHolder === undefined) {
      this.captionSpotHolder = [];
      // MMU,DGTの識別子を追加
      const productViewType = this.getProductViewType();
      this.config.captions.forEach(captionConf => {
        if (productViewType) {
          captionConf.productViewType = productViewType;
        }
        this.captionSpotHolder.push(new InfoCaptionSpot(this.panorama, captionConf));
      });
    } else {
      this.captionSpotHolder.forEach(captionSpot => {
        captionSpot.show();
      });
    }
  }


  hideCaptions() {
    if (!this.hasCaptions) return;
    if (this.captionSpotHolder === undefined) return;
    this.captionSpotHolder.forEach(spot => {
      spot.hide();
    });
  }


  setupProductView() {
    if (!this.hasProductView) return;
    if (this.productViewSpotHolder === undefined) {
      this.productViewSpotHolder = [];
      this.config.productView.forEach(productViewConf => {
        this.productViewSpotHolder.push(new InfoProductViewSpot(this.panorama, productViewConf));
      });
    } else {
      this.productViewSpotHolder.forEach(productViewSpot => {
        productViewSpot.show();
      });
    }
  }


  hideProductView() {
    if (!this.hasProductView) return;
    if (this.productViewSpotHolder === undefined) return;
    this.productViewSpotHolder.forEach(spot => {
      spot.hide();
    });
  }


  setupVideo() {
    if (!this.hasVideo) return;
    if (this.videoSpotHolder === undefined) {
      this.videoSpotHolder = [];
      this.config.video.forEach(videoConf => {
        this.videoSpotHolder.push(new InfoVideoLabelSpot(this.panorama, videoConf));
      });
    } else {
      this.videoSpotHolder.forEach(videoSpot => {
        videoSpot.show();
      });
    }
  }


  hideVideo() {
    if (!this.hasVideo) return;
    if (this.videoSpotHolder === undefined) return;
    this.videoSpotHolder.forEach(spot => {
      spot.hide();
    });
  }


  setupLinking() {
    if (this.linkHolder === undefined) {
      this.linkHolder = [];
      this.config.links.forEach(linkConf => {
        const panoCtlTo = AppManager.getPanoramaById(linkConf.id);
        if (panoCtlTo) {
          this.linkHolder.push(new InfoLinkSpot(this, panoCtlTo, linkConf));
        }
      });
    } else {
      this.linkHolder.forEach(link => {
        link.show();
      });
    }
  }


  setupFov() {
    if (this.config.fov === undefined) return;
    this.viewer.setCameraFov(parseInt(this.config.fov));
  }


  hideLinks() {
    if (this.linkHolder === undefined) return;
    this.linkHolder.forEach(link => {
      link.hide();
    });
  }


  registerInteractiveSpot(spot) {
    if (this.interactiveHolder === undefined) this.interactiveHolder = [];
    if (this.interactiveHolder.findIndex(element => element === spot) >= 0) return;
    this.interactiveHolder.push(spot);
    spot.hitArea.userData.owner = spot;
  }


  onHover(event) {
    if (this.interactiveHolder === undefined) return;

    const intersectHit = event.intersects.find(ist => {
      return this.interactiveHolder.findIndex(element =>
        element.hitArea && ist.object === element.hitArea) >= 0;
    });

    if ((this.selectedInteractive && event.intersects.length > 0 && intersectHit && this.selectedInteractive !== intersectHit.object)
      || (this.selectedInteractive && event.intersects.length == 0)) {
      AppManager.viewer.getContainer().style.cursor = 'default';
      this.selectedInteractive.userData.owner.onHoverEnd(event);
      this.selectedInteractive = undefined;
    }

    if (intersectHit) {
      this.selectedInteractive = intersectHit.object;
      AppManager.viewer.getContainer().style.cursor = 'pointer';
      this.selectedInteractive.userData.owner.onHoverStart(event);
    }
  }


  getProductViewType() {
    if (!this.hasProductView) return null;
    const { action } = this.config.productView[0];
    if (!action || !action.content) return null;
    return action.content;
  }


  openCommentary() {
    if (!this.config.commentary) return;
    if (this.config.commentary.video) {
      const action = {
        type: "modal--video",
        content: AppManager.convertToLocalizedPath(this.config.commentary.video),
      };
      PopupManager.openVideo(action);
    } else if (this.config.commentary.image) {
      PopupManager.openImage(
        AppManager.convertToLocalizedPath(this.config.commentary.image)
      );
    }
  }


  debugLog() {
    logger.info(`Spot | ${this.panoramaId} | ${event.type} |`, event);
  }


  get panoramaId() {
    return this.config.id;
  }


  get linkLabel() {
    if (this.config['linkLabel_' + AppManager.currentLanguage]) {
      return this.config['linkLabel_' + AppManager.currentLanguage];
    }
    return this.config.linkLabel;
  }


  get linkLabelImage() {
    if (this.config.linkLabelImage) {
      return AppManager.convertToLocalizedPath(this.config.linkLabelImage);
    }
    return null;
  }


  get hasMenu() {
    return this.config.hasOwnProperty('menu') && this.config['menu'].length > 0;
  }


  get hasCaptions() {
    return this.config.hasOwnProperty('captions') && this.config['captions'].length > 0;
  }


  get hasProductView() {
    return this.config.hasOwnProperty('productView') && this.config['productView'].length > 0;
  }


  get hasVideo() {
    return this.config.hasOwnProperty('video') && this.config['video'].length > 0;
  }


}
